// ---------------------------------------------------------------------------
// abox-app-deeplink — the CLOSE-01 integration half (Phase 033 Plan 02 /
// D-CLOSE01-A). Wires the Plan 033-01 `createAboxLoadedAppLoader` into the
// studio via a URL-param deeplink, gated + boot-race-safe + additive.
//
// Trigger:  /studio?app_id=<APP>&tenant_id=<UUID>[&branch=main]
// Gate A:   isLiveAboxEnabled()         (HARD VITE_E2E_MOCK guard FIRST —
//                                         keeps the 254-suite on TTL; R-033-03)
// Gate B:   deriveTenantId(session)     (null → no-op; NEVER "")
// Loader:   createAboxLoadedAppLoader({ supabaseClient: supabase, ... })
//           under the authenticated @/lib/supabase session client (R-033-02)
// Write:    useStudioStore.getState().openAboxApp(loaded, appId)  (ADDITIVE
//           project-slice action — openProject/loadTemplate/openServerProject
//           stay byte-identical, R-033-03)
//
// Security (threat T-033-06 — elevation of privilege): the loader's tenantId
// argument is `deriveTenantId(session)` (the session JWT claim), NEVER the
// raw `?tenant_id=` URL param. The URL param is ONLY the deeplink trigger;
// if it diverges from the session claim, the session claim wins. The URL
// param is user-controlled and must never directly parameterize the tenant
// filter (Phase 417 RLS independently enforces server-side isolation).
//
// Boot-race (Phase 031 Plan 05 / Codex Finding 1 analog): App.tsx runs
// `initAuth()` in a SEPARATE mount effect from this deeplink effect, so on
// the live boot path the session may be null when the deeplink handler first
// fires. We reuse the EXACT `subscribeAndRetry()` idiom shape from
// `mutation-client-init.ts:170-181`: subscribe to `useStudioStore.session`
// and re-run the handler on the null→populated transition exactly once. NO
// polling, NO setTimeout, NO magic-number delays (CLAUDE.md Bug-Fix rules).
//
// Non-goal (D-DEFER02 Hard NO-converge): this handler is one-shot
// reload-on-load BY CONSTRUCTION. It loads the assembled app ONCE per
// gated deeplink and writes it into `currentProject`. There is NO reactive
// Realtime-subscription Preview path here — DEFER-02 (live cross-session
// Preview refresh) is NOT an extension of this loader; a future v2.5+ planner
// must build a NET-NEW reactive path (a Realtime channel feeding an
// invalidate()+reload, not a deeper wire into this one-shot handler).
// ---------------------------------------------------------------------------

import { supabase } from '@/lib/supabase'
import { deriveTenantId } from '@/store/auth-slice'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { isLiveAboxEnabled } from './abox-flag'
import { createAboxLoadedAppLoader } from './abox-loaded-app-loader'
import { prepareAboxAppForRender } from './abox-render-prep'

const DEFAULT_BRANCH = 'main'

export interface AboxDeeplink {
  appId: string
  tenantId: string
  branch: string
}

/**
 * Parse `?app_id=` + `?tenant_id=` (+ optional `?branch=`, defaulted to
 * `'main'`) out of a URL search string.
 *
 * Returns `null` unless BOTH `app_id` and `tenant_id` are present — partial
 * deeplinks never trigger a load (no ambiguous/half loads; threat T-033-05:
 * URLSearchParams parsing, no string-built filters).
 *
 * NOTE: the returned `tenantId` is the RAW URL param. It is the deeplink
 * TRIGGER only — the handler does NOT pass it to the loader (it derives the
 * trusted tenant from the session JWT instead). It is surfaced here purely
 * so a caller can detect the deeplink shape.
 */
export function parseAboxDeeplink(search: string): AboxDeeplink | null {
  const params = new URLSearchParams(search)
  const appId = params.get('app_id')
  const tenantId = params.get('tenant_id')
  if (!appId || !tenantId) return null
  const branch = params.get('branch') || DEFAULT_BRANCH
  return { appId, tenantId, branch }
}

/**
 * Idempotency / boot-race guards. Production code MUST NOT reset these —
 * exported solely for unit tests (mirrors
 * `_resetMutationClientInitForTest()`, Phase 031 Plan 05 precedent).
 */
let _loaded = false
let _subscribed = false

/**
 * Reset the guards. PRODUCTION CODE MUST NOT CALL THIS — test seam only.
 */
export function _resetAboxDeeplinkForTest(): void {
  _loaded = false
  _subscribed = false
}

/**
 * Run the gated ABox deeplink load for the given URL search string.
 *
 * Sequence (gate order is load-bearing — R-033-03 / threat T-033-07):
 *   1. `if (!isLiveAboxEnabled()) return` — HARD VITE_E2E_MOCK guard FIRST.
 *      The 254-suite runs with VITE_E2E_MOCK=true so the path is never
 *      entered there with zero per-test config.
 *   2. parse the deeplink; `if (!parsed) return` (no partial loads).
 *   3. `const tenantId = deriveTenantId(session)`; if null, install the
 *      boot-race subscription (so a later sign-in retries) and return —
 *      NEVER call the loader with a null/"" tenant.
 *   4. invoke the loader under the authenticated `@/lib/supabase` client
 *      with the SESSION-derived tenantId (NOT the URL param — T-033-06).
 *   5. `await loader.load()`, then write the LoadedApp into
 *      `currentProject` via the additive `openAboxApp` slice action.
 *
 * Idempotent: a second call after a successful load is a no-op (`_loaded`
 * guard) so the boot-race retry + a re-render of the App.tsx effect cannot
 * double-load.
 */
export function handleAboxDeeplink(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): void {
  if (_loaded) return

  // Gate A — HARD VITE_E2E_MOCK guard FIRST (must run before anything else
  // so a stray live-flag in the e2e env cannot reach the live backend AND
  // so the 254-suite never installs the boot-race subscription).
  if (!isLiveAboxEnabled()) return

  // Parse the deeplink — both params required (no partial loads).
  const parsed = parseAboxDeeplink(search)
  if (!parsed) return

  // Gate B — session-derived tenant. The session JWT claim is the ONLY
  // trusted tenant source (T-033-06). null → no-op (NEVER "").
  const tenantId = deriveTenantId(useStudioStore.getState().session)
  if (tenantId === null) {
    // Boot-race: the session may still be loading (App.tsx runs initAuth()
    // in a separate mount effect). Install the session→retry subscription
    // so the null→populated transition re-runs the handler exactly once.
    subscribeAndRetry(search)
    return
  }

  // Mark loaded BEFORE the async work so a synchronous re-entry (e.g. the
  // boot-race listener firing while load() is in flight) cannot double-load.
  _loaded = true

  const loader = createAboxLoadedAppLoader({
    supabaseClient: supabase,
    // Tenant precedence (T-033-06): the SESSION claim, NOT parsed.tenantId.
    tenantId,
    appId: parsed.appId,
    branch: parsed.branch,
  })

  loader
    .load()
    .then((loaded) => {
      // Phase 046 (D-046-01/02) — the Phase 045 loader produces only a
      // config-only minimal `ui.root`; `prepareAboxAppForRender` synthesizes
      // a faithful renderable `ui` per `block_type` BEFORE the LoadedApp is
      // written to `currentProject` so Preview shows the operator's authored
      // app (not a stub/placeholder).
      const prepared = prepareAboxAppForRender(loaded)
      useStudioStore.getState().openAboxApp(prepared, parsed.appId)
    })
    .catch((err: unknown) => {
      // Reset so a later retry (e.g. operator re-navigates) can try again —
      // a transient PostgREST error must NOT permanently wedge the deeplink.
      _loaded = false
      // D-046-07 — surface the failure as a non-blocking toast (an internal-
      // tool operator must be able to tell a failed load from a genuinely
      // empty app). The `<Toaster />` renders the message as React text
      // content — no markup injection (threat T-046-06).
      const reason = err instanceof Error ? err.message : String(err)
      toast.error(`Couldn't load app ${parsed.appId}: ${reason}`)
      // D-046-08 — clear Preview to a neutral empty state so a stale template
      // / prior project is not mistaken for the deeplinked app (threat T-046-07).
      useStudioStore.getState().clearProjectToEmpty()
      // Keep the console trace too (unchanged from CLOSE-01).
      console.error('[abox-app-deeplink] load failed:', err)
    })
}

/**
 * Install the session→retry subscription. Idempotent: subscribes at most
 * once per page load (`_subscribed` guard prevents HMR / re-render leaks).
 * Reuses the EXACT `subscribeAndRetry()` shape from
 * `mutation-client-init.ts:170-181` — `useStudioStore.subscribe((s)=>s.session,
 * (session)=>{ if (session) handleAboxDeeplink() })`. The retry is safe
 * because `handleAboxDeeplink` is itself idempotent + flag-gated +
 * tenant-gated, so duplicates short-circuit.
 */
function subscribeAndRetry(search: string): void {
  if (_subscribed) return
  _subscribed = true
  useStudioStore.subscribe(
    (s) => s.session,
    (session) => {
      if (session) {
        handleAboxDeeplink(search)
      }
    },
  )
}
